import React, { memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import Iframe from 'app/shared/components/common/Iframe';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import { LOAN_EDITOR_CUSTOM_FORM_IFRAME_DEFAULT_HEIGHT_PX } from 'constants/loanEditorCustomForms';
import { substituteLoanTokens } from 'utils/loanEditorCustomForms';

import {
  IFRAME_REFERRER_POLICY,
  IFRAME_SANDBOX,
  IFRAME_SECTION_MESSAGES,
} from './IframeSection.constants';
import {
  getIframeFrameStyles,
  iframeMissingUrlStyles,
  iframeSectionStyles,
  iframeSectionTitleStyles,
  iframeStyles,
} from './IframeSection.styles';

const resolveHeightInPx = iframeHeightPx => {
  const numeric = Number(iframeHeightPx);

  if (!Number.isFinite(numeric) || numeric <= 0) {
    return LOAN_EDITOR_CUSTOM_FORM_IFRAME_DEFAULT_HEIGHT_PX;
  }

  return Math.round(numeric);
};

const IframeSection = ({ title, iframeUrl, iframeHeightPx, tokenContext }) => {
  const resolvedUrl = useMemo(
    () => substituteLoanTokens(iframeUrl, tokenContext),
    [iframeUrl, tokenContext],
  );
  const heightInPx = resolveHeightInPx(iframeHeightPx);

  return (
    <Box sx={iframeSectionStyles}>
      <Box sx={iframeSectionTitleStyles}>
        <Typography variant="bold14">
          {title || IFRAME_SECTION_MESSAGES.UNTITLED_SECTION}
        </Typography>
      </Box>

      {isEmpty(resolvedUrl) ? (
        <Box sx={iframeMissingUrlStyles}>
          <Typography color="text.secondary" variant="regular12">
            {IFRAME_SECTION_MESSAGES.MISSING_URL}
          </Typography>
        </Box>
      ) : (
        <Box sx={getIframeFrameStyles({ heightInPx })}>
          <Iframe
            height="100%"
            referrerPolicy={IFRAME_REFERRER_POLICY}
            sandbox={IFRAME_SANDBOX}
            src={resolvedUrl}
            style={iframeStyles}
            title={title || IFRAME_SECTION_MESSAGES.UNTITLED_SECTION}
            width="100%"
          />
        </Box>
      )}
    </Box>
  );
};

IframeSection.propTypes = {
  title: PropTypes.string,
  iframeUrl: PropTypes.string,
  iframeHeightPx: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tokenContext: PropTypes.shape({
    loanId: PropTypes.string,
    loanNumber: PropTypes.string,
    borrowerId: PropTypes.string,
  }),
};

export default memo(IframeSection);
